/**
 * Trunnion AI - Audit Log Viewer
 * Searchable, filterable audit log table.
 */

import { useState } from 'react';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Badge } from '@/components/ui/Badge';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import { AuditFilterBar } from './AuditFilterBar';
import { AuditExportButton } from './AuditExportButton';
import { useAuditLogList } from '@/hooks/queries/useAuditLogs';
import type { AuditEvent, AuditLogFilters, ClassificationLevel } from '@/types';

export function AuditLogViewer() {
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    pageSize: 25,
  });

  const { data, isLoading } = useAuditLogList(filters);

  const columns: Column<AuditEvent>[] = [
    {
      key: 'timestamp',
      header: 'Timestamp',
      sortable: true,
      width: '180px',
      render: (event) => (
        <span className="text-xs text-trunnion-slate-300 font-mono">
          {new Date(event.timestamp).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'eventType',
      header: 'Event',
      sortable: true,
      render: (event) => (
        <Badge
          variant={
            event.eventType.includes('fail') || event.eventType.includes('denied') || event.eventType.includes('violat')
              ? 'error'
              : event.eventType.includes('complete') || event.eventType.includes('login')
                ? 'success'
                : 'default'
          }
        >
          {event.eventType}
        </Badge>
      ),
    },
    {
      key: 'actor',
      header: 'Actor',
      render: (event) => (
        <div>
          <span className="text-sm text-white">{event.actorName}</span>
          <span className="ml-1.5 text-xs text-trunnion-slate-500">
            ({event.actorType})
          </span>
        </div>
      ),
    },
    {
      key: 'resource',
      header: 'Resource',
      render: (event) => (
        <div>
          <span className="text-sm text-trunnion-slate-200">{event.resourceName}</span>
          <span className="ml-1.5 text-xs text-trunnion-slate-500">
            {event.resourceType}
          </span>
        </div>
      ),
    },
    {
      key: 'classification',
      header: 'Level',
      width: '100px',
      render: (event) => (
        <ClassificationTag level={event.classification as ClassificationLevel} />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <AuditFilterBar filters={filters} onChange={setFilters} />
        <AuditExportButton />
      </div>

      <DataTable<AuditEvent>
        columns={columns}
        data={data?.events ?? []}
        keyExtractor={(e) => e.id}
        loading={isLoading}
        emptyMessage="No audit events found"
        page={filters.page ?? 1}
        pageSize={filters.pageSize ?? 25}
        total={data?.total}
        onPageChange={(page) => setFilters({ ...filters, page })}
      />
    </div>
  );
}
